import { type FrameName } from "@/game/entities/config";
import type { ResourceType } from "@/game/entities/config/resources";
import type TileEntity from "@/game/entities/TileEntity";

export type TileIndex = number;

export type TilePhaserIndex = number;

export type TileId = number;

export type TileCoordinates = {
	x: number;
	y: number;
};

export type TileCoordinatesWithValue = {
	x: number;
	y: number;
	value: boolean;
};

// TODO: Toto cele treba prerobit na entity, ktore uz maju vsetky povodne params.
export type TileNeighbour = {
	tileIndex: TileIndex;
	tileId: TileId;
	frameName: FrameName;
	entity?: TileEntity;
};

export type ProductionBase = Record<
	ResourceType,
	{ value: number; frameName: FrameName }
>;

export type ProductionDefaultData = {
	number: number;
	icon: ResourceType;
	label: string;
};

export type ProductionValueCallback = (entity: TileEntity) => number;

export type ProductionValue = number | ProductionValueCallback;

export type SuppliesValueCallback = () =>
	| FrameName
	| Array<FrameName>
	| Set<FrameName>
	| Iterable<FrameName>
	| null;

export type SuppliesValue = FrameName | SuppliesValueCallback;

export enum UpgradeObjectiveAction {
	BUILDING_COUNT = 1,
	EXPLORED_COUNT,
	INCOME,
	// ROAD_PERCENTAGE,
	SETTLEMENT_BUILDING_COUNT,
	SETTLEMENT_CLICK_COUNT,
	SETTLEMENT_COUNT,
	SETTLEMENT_EXPLORED_RANGE,
	SETTLEMENT_ON_LEVEL_COUNT,
	SETTLEMENT_ONCLICK_COINS_ACCUMULATED,
	SETTLEMENT_CLEARED_RANGE,
	SETTLEMENT_LEVEL_ACCUMULATED,
	/*
	// - ked som offline, tak dostavam 100% income do tohto questu. Malo by sa to prenasobit, mojou realnou hodnotou offline income
	// - progres sa nadalej dava aj ked odstranim budovu, ktora v meste produkovala goods
	// - a este som si vsimol taku drobnost, ze ked som offline len 1 tick, tak sa neurobi ziadny progres aj ked dostanem offline income okno
	SETTLEMENT_INCOME,
	*/
	BUILDING_INCOME,
	UNLOCKED_UPGRADE_COUNT,
}

export type UpgradeObjectiveNumberData = Record<
	"{level}" | "{number}" | "{2to4}" | "{one}" | "{percent}",
	number
>;

export type UpgradeObjectiveFrameNameData = Record<
	"{terrains}" | "{buildings}",
	Set<FrameName>
>;

export type UpgradeObjectiveResourceData = Record<"{resource}", ResourceType>;

export enum UpgradeObjectiveType {
	MAIN = "main",
	SIDE = "side",
}

export type UpgradeObjective = {
	text: string;
	action: UpgradeObjectiveAction;
	hint?: string;
	data?: Partial<
		UpgradeObjectiveNumberData &
			UpgradeObjectiveFrameNameData &
			UpgradeObjectiveResourceData
	>;
};

export type ResourceValue = number | ((entity: TileEntity) => number);

export type UpgradeRewardValue = number | (() => number);

export type UpgradeParams = {
	objective?: {
		[UpgradeObjectiveType.MAIN]: UpgradeObjective;
		[UpgradeObjectiveType.SIDE]?: UpgradeObjective;
	};
	price?: Partial<Record<ResourceType, ResourceValue>>;
	skipPrice?: Partial<Record<ResourceType, number>>; // gems only
	reward?: Partial<Record<ResourceType, UpgradeRewardValue>>;
	text?: string;
	onUpgrade?: () => void;
	mutate0?: FrameName; // Toto zabezpeci, ze sa teren pod budovou zmeni na tento FrameName. Cenovka uz musi byt zahrnuta v budove. Aktualne sa nepouziva, kedze lesy, kde to bolo sa revertli, lebo to nemyslelo na QT.
};

export type UpgradeablePropertyKey = "base" | `upgrade.${number}`;

export type Reward = Partial<Record<ResourceType, number>>;

export type TileEntityOnClickMethodReturnType = {
	reward?: Reward;
};

export type TileEntityOnRevealMethodReturnType = {
	reward?: Reward;
};

export enum TileAction {
	CUT = "cut",
	REMOVE = "remove",
	SAVE_MATERIAL = "save_material_action",
	HIRE = "hire_action",
	TRADING = "trade_action",
	CLEAR = "clear_action",
	DEMOLISH = "demolish",
	DEFEAT = "defeat",
	MOVE = "move",
	DISBAND = "disband",
}

export enum BuffType {
	// aura
	ADD_ARMOR = "add_armor",
	ADD_ATTACK = "add_attack",
	LOWER_ARMOR = "lower_armor",
	LOWER_ATTACK = "lower_attack",
	ADD_ARMOR_SAME = "add_armor_same",
	ADD_ATTACK_SAME = "add_attack_same",
	LOWER_SUPPLY_COST = "lower_supply_cost",
	ADD_HP = "add_hp",
	ADD_HP_SAME = "add_hp_same",
	ADD_ARMOR_MELEE = "add_armor_melee",
	ADD_ARMOR_RANGED = "add_armor_ranged",
	ADD_ARMOR_SIEGE = "add_armor_siege",
	// self
	ISOLATION_ATTACK = "isolation_attack",
	ISOLATION_ARMOR = "isolation_armor",
	ENGAGEMENT_ATTACK = "engagement_attack",
	ENGAGEMENT_ARMOR = "engagement_armor",
	BATTLE_FRENZY_ATTACK = "battle_frenzy_attack",
	BATTLE_FRENZY_ARMOR = "battle_frenzy_armor",
}

export const auraBuffTypes = new Set<BuffType>([
	BuffType.ADD_ARMOR,
	BuffType.ADD_ATTACK,
	BuffType.LOWER_ARMOR,
	BuffType.LOWER_ATTACK,
	BuffType.ADD_ARMOR_SAME,
	BuffType.ADD_ATTACK_SAME,
	BuffType.LOWER_SUPPLY_COST,
	BuffType.ADD_HP,
	BuffType.ADD_HP_SAME,
	BuffType.ADD_ARMOR_MELEE,
	BuffType.ADD_ARMOR_RANGED,
	BuffType.ADD_ARMOR_SIEGE,
]);

export const selfBuffTypes = new Set<BuffType>([
	BuffType.ISOLATION_ATTACK,
	BuffType.ISOLATION_ARMOR,
	BuffType.ENGAGEMENT_ATTACK,
	BuffType.ENGAGEMENT_ARMOR,
	BuffType.BATTLE_FRENZY_ATTACK,
	BuffType.BATTLE_FRENZY_ARMOR,
]);

export type Buff = {
	type: BuffType;
	value: number;
};

export type ArmorOrAttackObject = {
	melee: number;
	ranged: number;
	siege: number;
};

export type ArmorOrAttackNumber = number;

export type ArmorOrAttackValue = ArmorOrAttackObject | ArmorOrAttackNumber;

export type TileConfigItem = {
	requirements: Map<ResourceType, number>;
	production: Map<ResourceType, ProductionValue>;
	productionBase?: Partial<
		Record<ResourceType, { value: number; frameName?: FrameName }>
	>;
	consumption: Map<ResourceType, number>;
	storage: Map<ResourceType, number>;
	upgrades: Map<FrameName, UpgradeParams>;
	roadCaps?: {
		[FrameName.GRAVEL_ROAD]: number;
		[FrameName.COBBLED_ROAD]: number;
	};
	supplies?: Set<SuppliesValue>;

	// only for terrain
	exploreCoefficient?: number;

	// only for settlements
	onClick?: () => TileEntityOnClickMethodReturnType;

	// only for salvageables
	isVisible?: boolean;
	onReveal?: () => void;
	onUpgrade?: (upgradedTile: TileCoordinates) => void;
	action?: TileAction;
	reward?: Reward;
	rewardRange?: Map<ResourceType, { min: number; max: number }>;

	// only for POI
	rewardText?: Array<string>;
	rewardTextReplace?: Array<{ [key: string]: string }>;

	// only for buildings
	range?: number;
	onTick?: () => void;
	recommendedUpgrades?: Array<number>;
	inSearchTool?: boolean;

	// player / enemy units
	siegeAttributes?: {
		maxHp: number;
		armor: ArmorOrAttackValue;
		attack: ArmorOrAttackValue;
		supplyLimit?: number;
		attackClicks?: number;
		buildingLimitFrameName?: FrameName | null;
		grantBuffs: Array<Buff>;
	};
};

export type TileConfig = Partial<Record<FrameName, TileConfigItem>>;

export enum TileEntityParam {
	VISIBILITY = "visibility",
	DURABILITY = "durability",
	IS_CONNECTED_TO_MAIN_SETTLEMENT = "isConnectedToMainSettlement",
	IS_BLOCKED_BY_ENEMY = "isBlockedByEnemy",
}

export type TileEntityParams = {
	[TileEntityParam.VISIBILITY]: Uint8Array;
	[TileEntityParam.DURABILITY]: Uint32Array;
	[TileEntityParam.IS_CONNECTED_TO_MAIN_SETTLEMENT]: Uint8Array;
	[TileEntityParam.IS_BLOCKED_BY_ENEMY]: Uint8Array;
};

export type SettlementItem = TileCoordinates & { name: string };

export type ToastTypeConfig = {
	textColor: string;
	// TODO: ikona nebude v texte ale vzdy mimo ?
	icon?: string; // optional icon
	hasBackground: boolean;
	backgroundColor?: number; // if there is background, define color
	additionalTextColor?: string; // additional text color for the toast
};

export type TileLabelZoomBehavior = {
	/** Dynamic scale mode: (1 / max(zoom, minZoom)) * scaleCoefficient * dpr. Enables dynamic scale mode when defined. */
	scaleCoefficient?: number;
	/** Base Y-offset for dynamic scale mode */
	offsetBase?: number;
	/** Alt Y-offset used when the main settlement name label is visible */
	tutorialOffsetWithSettlementName?: number;
	/** When true: use fixed offset * dpr at max zoom instead of dynamic formula */
	fixedOffsetAtMaxZoom?: boolean;
	/** Hide label if cameraZoom < tileLabelZoomConfig[hideBelowZoomIndex] */
	hideBelowZoomIndex?: number;
	/** Hide label if cameraZoom <= threshold[devicePixelRatio]; unknown DPR falls back to 0.1 */
	hideBelowThresholdByDpr?: Record<number, number>;
	/** Override visibility with the hideLabel flag (e.g. close-button dismiss) */
	respectsHideLabel?: boolean;
	/**
	 * Per-tier scale multiplier applied on top of the base scale for that tier:
	 *   0 → zoom >= zoomConfig[1]          (base: 1)
	 *   1 → zoomConfig[2] ≤ zoom < [1]     (base: tileLabelZoomBehaviorDefaults.midZoomBase)
	 *   2 → zoomConfig[3] ≤ zoom < [2]     (base: auto-scale formula)
	 *   3 → zoom < zoomConfig[3]           (base: auto-scale formula at min)
	 */
	zoomTierMultipliers?: Partial<Record<0 | 1 | 2 | 3, number>>;
	/** Coefficient for the (1 / zoom) * coeff * dpr auto-scale formula. Default: 0.17 */
	autoScaleCoefficient?: number;
};

export type TileLabelTypeConfig = {
	asset: number;
	backgroundFrameMain: number;
	backgroundFrameAlt: number;
	fontSize?: number;
	fontColor?: number;
	tileSizeScaleMin?: boolean; // ci musi byt minimalne tile size
	yOffset?: number;
	zoom?: TileLabelZoomBehavior;
	defaultText?: string;
};

export enum TileLabelSkin {
	LEVEL = "level",
	TUTORIAL = "tutorial",
	QUEST_MAIN_Q = "questMainQ",
	QUEST_MAIN_E = "questMainE",
	QUEST_SIDE_Q = "questSideQ",
	QUEST_SIDE_E = "questSideE",
	CHEST_DARK = "chestDark",
	CHEST = "chest",
	GEMS_BADGE = "gemsBadge",
	SETTLEMENT_HIGHLIGHT = "settlementHighlight",
	MAIN_SETTLEMENT_NAME = "mainSettlementName",
	SPECIAL_LOCATION = "specialLocation",
	SEARCH_TOOL = "searchTool",
	EXPANSION_ARROW_UP = "expansionArrowUp",
	EXPANSION_ARROW_RIGHT = "expansionArrowRight",
	EXPANSION_ARROW_DOWN = "expansionArrowDown",
	EXPANSION_ARROW_LEFT = "expansionArrowLeft",
	EASTER_EGG = "easterEgg",
	EASTER_EGG_GOLDEN = "easterEggGolden",
	HALLOWEEN_PUMPKIN = "halloweenPumpkin",
	HALLOWEEN_JACK_O_LANTERN = "halloweenJackOLantern",
	PIRATES_MAP = "piratesMap",
	PIRATES_TREASURE = "piratesTreasure",
	PLAGUE_GREEN = "plagueGreen",
	PLAGUE_WHITE = "plagueWhite",
	FIGHT_SYSTEM_1 = "fightSystem1",
	FIGHT_SYSTEM_2 = "fightSystem2",
	FIGHT_SYSTEM_3 = "fightSystem3",
	FIGHT_SYSTEM_4 = "fightSystem4",
	FIGHT_SYSTEM_ALL = "fightSystem1|fightSystem2|fightSystem3|fightSystem4",
	FIGHT_SYSTEM_DEFEATED_ENEMY = "fightSystemDefeatedEnemy",
	FIGHT_SYSTEM_DEFEATED_FRIENDLY = "fightSystemDefeatedFriendly",
}

export enum TileLabelType {
	MAIN_SETTLEMENT_NAME = "mainSettlementName",
	MAIN_SETTLEMENT = "mainSettlement",
	SETTLEMENT = "settlement",
	SETTLEMENT_NOT_CONNECTED = "settlementNotConnected",
	MISC = "misc",
}

export const tileLabelScaleGemsMultiplier = 4;

export const tileLabelTypes: Record<string, TileLabelTypeConfig> = {
	[TileLabelType.MAIN_SETTLEMENT_NAME]: {
		asset: 1,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		fontSize: 28,
		fontColor: 0xf4d278,
		zoom: {
			scaleCoefficient: 0.6,
			offsetBase: 40,
			fixedOffsetAtMaxZoom: true,
			hideBelowThresholdByDpr: { 1: 0.11, 2: 0.11, 3: 0.17 },
		},
	},
	[TileLabelType.MAIN_SETTLEMENT]: {
		asset: 1,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 2,
		fontSize: 208,
		fontColor: 0x372615,
	},
	[TileLabelType.SETTLEMENT]: {
		asset: 1,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 3,
		fontSize: 208,
		fontColor: 0x372615,
	},
	[TileLabelType.MISC]: {
		asset: 1,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
	},
	[TileLabelSkin.TUTORIAL]: {
		asset: 1,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		zoom: {
			scaleCoefficient: 0.3,
			offsetBase: 40,
			tutorialOffsetWithSettlementName: 90,
			hideBelowZoomIndex: 2,
			respectsHideLabel: true,
		},
	},
	[TileLabelSkin.QUEST_MAIN_Q]: {
		asset: 1,
		backgroundFrameMain: 4,
		backgroundFrameAlt: 4,
		tileSizeScaleMin: true,
		zoom: {
			zoomTierMultipliers: {
				2: 1.2,
				3: 2.4,
			},
		},
	},
	[TileLabelSkin.QUEST_MAIN_E]: {
		asset: 1,
		backgroundFrameMain: 5,
		backgroundFrameAlt: 5,
		tileSizeScaleMin: true,
		zoom: {
			zoomTierMultipliers: {
				2: 1.2,
				3: 2.4,
			},
		},
	},
	[TileLabelSkin.QUEST_SIDE_Q]: {
		asset: 1,
		backgroundFrameMain: 6,
		backgroundFrameAlt: 6,
		tileSizeScaleMin: true,
		zoom: {
			zoomTierMultipliers: {
				2: 1.2,
				3: 2.4,
			},
		},
	},
	[TileLabelSkin.QUEST_SIDE_E]: {
		asset: 1,
		backgroundFrameMain: 7,
		backgroundFrameAlt: 7,
		tileSizeScaleMin: true,
		zoom: {
			zoomTierMultipliers: {
				2: 1.2,
				3: 2.4,
			},
		},
	},
	[TileLabelType.SETTLEMENT_NOT_CONNECTED]: {
		asset: 1,
		backgroundFrameMain: 8,
		backgroundFrameAlt: 3,
		fontSize: 208,
		fontColor: 0x372615,
	},
	[TileLabelSkin.CHEST_DARK]: {
		asset: 2,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.CHEST]: {
		asset: 2,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 1,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.GEMS_BADGE]: {
		asset: 2,
		backgroundFrameMain: 2,
		backgroundFrameAlt: 2,
		tileSizeScaleMin: true,
		zoom: {
			zoomTierMultipliers: {
				2: 2,
				3: tileLabelScaleGemsMultiplier,
			},
		},
	},
	[TileLabelSkin.SETTLEMENT_HIGHLIGHT]: {
		asset: 2,
		backgroundFrameMain: 3,
		backgroundFrameAlt: 3,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.SPECIAL_LOCATION]: {
		asset: 2,
		backgroundFrameMain: 4,
		backgroundFrameAlt: 4,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.SEARCH_TOOL]: {
		asset: 2,
		backgroundFrameMain: 5,
		backgroundFrameAlt: 5,
		tileSizeScaleMin: true,
		yOffset: 1,
	},
	[TileLabelSkin.EXPANSION_ARROW_UP]: {
		asset: 2,
		backgroundFrameMain: 6,
		backgroundFrameAlt: 6,
	},
	[TileLabelSkin.EXPANSION_ARROW_RIGHT]: {
		asset: 2,
		backgroundFrameMain: 7,
		backgroundFrameAlt: 7,
	},
	[TileLabelSkin.EXPANSION_ARROW_DOWN]: {
		asset: 2,
		backgroundFrameMain: 8,
		backgroundFrameAlt: 8,
	},
	[TileLabelSkin.EXPANSION_ARROW_LEFT]: {
		asset: 2,
		backgroundFrameMain: 9,
		backgroundFrameAlt: 9,
	},
	[TileLabelSkin.EASTER_EGG]: {
		asset: 31,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.EASTER_EGG_GOLDEN]: {
		asset: 31,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 1,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.PLAGUE_GREEN]: {
		asset: 4,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
	},
	[TileLabelSkin.PLAGUE_WHITE]: {
		asset: 4,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 1,
	},
	[TileLabelSkin.FIGHT_SYSTEM_1]: {
		asset: 101,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		defaultText: "ui.enemy_territory",
		zoom: {
			scaleCoefficient: 0.2,
			hideBelowThresholdByDpr: { 1: 0.11, 2: 0.11, 3: 0.17 },
		},
	},
	[TileLabelSkin.FIGHT_SYSTEM_2]: {
		asset: 102,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		defaultText: "ui.active_battlefield",
		zoom: {
			scaleCoefficient: 0.2,
			hideBelowThresholdByDpr: { 1: 0.11, 2: 0.11, 3: 0.17 },
		},
	},
	[TileLabelSkin.FIGHT_SYSTEM_3]: {
		asset: 103,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		defaultText: "ui.insufficient_supplies",
		zoom: {
			scaleCoefficient: 0.2,
			hideBelowThresholdByDpr: { 1: 0.11, 2: 0.11, 3: 0.17 },
		},
	},
	[TileLabelSkin.FIGHT_SYSTEM_4]: {
		asset: 104,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		defaultText: "ui.battle_concluded",
		zoom: {
			scaleCoefficient: 0.2,
			hideBelowThresholdByDpr: { 1: 0.11, 2: 0.11, 3: 0.17 },
		},
	},
	[TileLabelSkin.FIGHT_SYSTEM_DEFEATED_ENEMY]: {
		asset: 5,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 1,
		defaultText: "ui.defeated_enemy",
	},
	[TileLabelSkin.FIGHT_SYSTEM_DEFEATED_FRIENDLY]: {
		asset: 5,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		defaultText: "ui.defeated_friendly",
	},
	[TileLabelSkin.HALLOWEEN_PUMPKIN]: {
		asset: 32,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.HALLOWEEN_JACK_O_LANTERN]: {
		asset: 32,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 1,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.PIRATES_MAP]: {
		asset: 33,
		backgroundFrameMain: 0,
		backgroundFrameAlt: 0,
		tileSizeScaleMin: true,
	},
	[TileLabelSkin.PIRATES_TREASURE]: {
		asset: 33,
		backgroundFrameMain: 1,
		backgroundFrameAlt: 1,
		tileSizeScaleMin: true,
	},
};

export const tileLabelZoomConfig: Record<number, number> = {
	// Nic
	0: 1.0, // Level
	1: 0.6, // Roh
	2: 0.35, // Cele
	3: 0.095, // Autoscale
};

export const tileLabelZoomBehaviorDefaults = {
	hideBelowThresholdFallback: 0.1,
	offsetBase: 40,
	autoScaleCoefficient: 0.17,
	midZoomBase: 0.4,
} as const;
